/**
 * Cart-item selection for the Cut to Size page's price. Unlike the
 * machining charge ladder (a single uniform step), this ladder's step size
 * grows across five tiers (5p up to GBP20, 50p to GBP100, GBP2 to GBP500,
 * GBP5 to GBP1,500, GBP10 to GBP3,500), so there's no fixed arithmetic
 * rounding formula - instead this scans the actual variant table for the
 * smallest available price at or above the target.
 *
 * window.CutToSizeChargeTable is rendered by
 * snippets/cut-to-size-charge-table.liquid.
 */
(function (window) {
  'use strict';

  var MAX_LINE_PENCE = 350000; // GBP3,500.00 - the ladder's own ceiling

  /**
   * The smallest table entry whose price is >= pence, or null if pence
   * exceeds every entry in the table.
   */
  function roundUpToTable(pence, table) {
    var best = null;
    for (var i = 0; i < table.length; i++) {
      if (table[i].pence >= pence && (best === null || table[i].pence < best.pence)) {
        best = table[i];
      }
    }
    return best;
  }

  /**
   * Picks the Cut to Size charge variant(s)/quantities needed to add an
   * exact charge (in GBP) to the cart. Rounds up to the nearest available
   * ladder price, and splits anything over the ladder's GBP3,500 ceiling
   * into one or more max-price lines plus a remainder line, the same way
   * the Machining Charge ladder handles amounts over its own ceiling.
   *
   * Each returned item carries its own `pence` alongside `id`/`quantity` so
   * callers can work out the exact total actually being charged (e.g. to
   * allocate it back across several parts) without re-deriving it from the
   * table themselves - strip it back out before sending items to
   * /cart/add.js, which only wants id/quantity/properties.
   *
   * @param {number} chargeGBP
   * @param {{pence:number, variantId:number}[]} table
   * @returns {{id:number, quantity:number, pence:number}[]}
   */
  function pickCutToSizeCartItems(chargeGBP, table) {
    var pence = Math.ceil(chargeGBP * 100);
    if (!(pence > 0)) return [];

    var items = [];
    var remaining = pence;

    if (remaining > MAX_LINE_PENCE) {
      var fullLines = Math.floor(remaining / MAX_LINE_PENCE);
      var maxEntry = roundUpToTable(MAX_LINE_PENCE, table);
      if (!maxEntry) {
        throw new Error('No Cut to Size charge variant found for the maximum line value.');
      }
      items.push({ id: maxEntry.variantId, quantity: fullLines, pence: maxEntry.pence });
      remaining -= fullLines * MAX_LINE_PENCE;
    }

    if (remaining > 0) {
      var entry = roundUpToTable(remaining, table);
      if (!entry) {
        throw new Error('No Cut to Size charge variant found for £' + (remaining / 100).toFixed(2));
      }
      items.push({ id: entry.variantId, quantity: 1, pence: entry.pence });
    }

    return items;
  }

  /**
   * The exact total pence actually charged by a set of cart items (as
   * returned by pickCutToSizeCartItems) - each item's pence x quantity,
   * summed.
   */
  function totalChargedPence(items) {
    return items.reduce(function (sum, item) {
      return sum + item.pence * item.quantity;
    }, 0);
  }

  /**
   * Splits an already-rounded total (in pence) back across several raw,
   * unrounded shares (also in pence) so the whole-penny amounts sum to
   * exactly totalPence - never more, never less. Each share gets its
   * proportional floor, then any leftover pence from rounding down go one
   * at a time to whichever share was closest to rounding up (the standard
   * "largest remainder" apportionment method), so the display for each
   * part stays as close as possible to its own fair share while the parts
   * always add up to the same total the customer is actually charged.
   *
   * @param {number[]} rawPenceShares
   * @param {number} totalPence
   * @returns {number[]} whole-penny amounts, same length and order as rawPenceShares
   */
  function allocatePence(rawPenceShares, totalPence) {
    var rawTotal = rawPenceShares.reduce(function (sum, p) {
      return sum + p;
    }, 0);
    if (!(rawTotal > 0)) return rawPenceShares.map(function () { return 0; });

    var shares = rawPenceShares.map(function (p) {
      return (p / rawTotal) * totalPence;
    });
    var allocated = shares.map(Math.floor);
    var remainder = totalPence - allocated.reduce(function (sum, p) { return sum + p; }, 0);

    var byRemainingFraction = shares
      .map(function (share, index) {
        return { index: index, fraction: share - allocated[index] };
      })
      .sort(function (a, b) {
        return b.fraction - a.fraction;
      });

    for (var i = 0; i < remainder; i++) {
      allocated[byRemainingFraction[i].index] += 1;
    }

    return allocated;
  }

  window.CutToSizeCharge = {
    pickCutToSizeCartItems: pickCutToSizeCartItems,
    totalChargedPence: totalChargedPence,
    allocatePence: allocatePence,
  };
})(window);
